import React, { memo } from 'react'
import { Col, Form, Input, Select, Spin, Divider } from 'antd'
import PropTypes from 'prop-types'
import { hexToRGBA } from '../../../../../lib/utils'
import CustomRow from '../../../../../reusable-components/CustomRow'

const MedicalAidSection = memo(
  ({
    form,
    hexToRGBA,
    token,
    residentVisitor,
    schemeData,
    schemeLoading,
    plansData,
    plansLoading,
    planOptionData,
    planOptionsLoading,
    debouncedSchemeFilter,
    setSchemeData,
    setPlansData,
    setPlanOptionData,
    handleGetMedicalAidPlans,
    handleGetMedicalAidPlanOptions,
    userData
  }) => {
    // Form item styles for consistent appearance
    const formItemStyle = {
      marginBottom: 16
    }

    return (
      <>
        <Col span={24}>
          <div style={{ width: '100%', display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ width: '100%', ...formItemStyle }}>
              <Form.Item
                label={<b style={{ fontSize: 15 }}>Medical Aid Scheme</b>}
                name={['medicalAid', 'name']}
                style={{ margin: 0 }}
              >
                <Select
                  showSearch
                  placeholder='Search for a Medical Aid Scheme'
                  optionFilterProp='label'
                  filterOption={false}
                  onSearch={debouncedSchemeFilter}
                  loading={schemeLoading}
                  notFoundContent={schemeLoading ? <Spin size='small' /> : null}
                  options={schemeData.map((scheme) => ({
                    value: scheme.name,
                    label: scheme.name,
                    data: JSON.stringify(scheme)
                  }))}
                  onChange={(value, option) => {
                    // Parse the scheme data
                    const schemeData = option?.data ? JSON.parse(option.data) : null

                    // Update the form with the scheme code
                    const updatedMedicalAid = {
                      ...form.getFieldValue('medicalAid'),
                      name: value,
                      schemeCode: schemeData ? schemeData.code : undefined,
                      plan: undefined,
                      planCode: undefined,
                      planOption: undefined,
                      planOptionCode: undefined
                    }

                    form.setFieldsValue({ medicalAid: updatedMedicalAid })

                    // Reset dependent dropdowns
                    setPlansData([])
                    setPlanOptionData([])

                    // Fetch plans if a scheme was selected
                    if (schemeData) {
                      handleGetMedicalAidPlans(schemeData.code)
                    }
                  }}
                  allowClear
                />
              </Form.Item>

              {/* Hidden field for scheme code */}
              <Form.Item name={['medicalAid', 'schemeCode']} hidden>
                <Input />
              </Form.Item>
            </div>
          </div>
        </Col>

        <Col span={24}>
          <div style={{ width: '100%', display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ width: '100%', ...formItemStyle }}>
              <Form.Item
                label={<b style={{ fontSize: 15 }}>Plan</b>}
                name={['medicalAid', 'plan']}
                style={{ margin: 0 }}
              >
                <Select
                  showSearch
                  placeholder='Select a Plan'
                  optionFilterProp='label'
                  filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
                  loading={plansLoading}
                  notFoundContent={plansLoading ? <Spin size='small' /> : null}
                  options={plansData.map((plan) => ({
                    value: plan.option,
                    label: plan.option,
                    data: JSON.stringify(plan)
                  }))}
                  onChange={(value, option) => {
                    // Parse the plan data
                    const planData = option?.data ? JSON.parse(option.data) : null

                    // Update the form with the plan code
                    const updatedMedicalAid = {
                      ...form.getFieldValue('medicalAid'),
                      plan: value,
                      planCode: planData ? planData.code : undefined,
                      planOption: undefined,
                      planOptionCode: undefined
                    }

                    form.setFieldsValue({ medicalAid: updatedMedicalAid })

                    // Reset plan options dropdown
                    setPlanOptionData([])

                    // Fetch plan options if a plan was selected
                    if (planData) {
                      handleGetMedicalAidPlanOptions(planData.code)
                    }
                  }}
                  disabled={!form.getFieldValue('medicalAid')?.schemeCode}
                  allowClear
                />
              </Form.Item>

              {/* Hidden field for plan code */}
              <Form.Item name={['medicalAid', 'planCode']} hidden>
                <Input />
              </Form.Item>
            </div>
          </div>
        </Col>

        <Col span={24}>
          <div style={{ width: '100%', display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ width: '100%', ...formItemStyle }}>
              <Form.Item
                label={<b style={{ fontSize: 15 }}>Plan Option</b>}
                name={['medicalAid', 'planOption']}
                style={{ margin: 0 }}
              >
                <Select
                  showSearch
                  placeholder='Select a Plan Option'
                  optionFilterProp='label'
                  filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
                  loading={planOptionsLoading}
                  notFoundContent={planOptionsLoading ? <Spin size='small' /> : null}
                  options={planOptionData.map((option) => ({
                    value: option.option,
                    label: option.option,
                    data: JSON.stringify(option)
                  }))}
                  onChange={(value, option) => {
                    // Parse the option data
                    const optionData = option?.data ? JSON.parse(option.data) : null

                    // Update the form with the plan option code
                    const updatedMedicalAid = {
                      ...form.getFieldValue('medicalAid'),
                      planOption: value,
                      planOptionCode: optionData ? optionData.code : undefined
                    }

                    form.setFieldsValue({ medicalAid: updatedMedicalAid })
                  }}
                  disabled={!form.getFieldValue('medicalAid')?.planCode}
                  allowClear
                />
              </Form.Item>

              {/* Hidden field for plan option code */}
              <Form.Item name={['medicalAid', 'planOptionCode']} hidden>
                <Input />
              </Form.Item>
            </div>
          </div>
        </Col>

        <Divider style={{ margin: '8px 0 16px' }} />

        <Col span={24}>
          <div style={{ width: '100%', display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ width: 'calc(50% - 8px)', minWidth: 200, ...formItemStyle }}>
              <Form.Item
                label={<b style={{ fontSize: 15 }}>Medical Aid Number</b>}
                name={['medicalAid', 'number']}
                style={{ margin: 0 }}
              >
                <Input placeholder='e.g. 123456789' />
              </Form.Item>
            </div>
            <div style={{ width: 'calc(50% - 8px)', minWidth: 200, ...formItemStyle }}>
              <Form.Item
                label={<b style={{ fontSize: 15 }}>Dependant Number</b>}
                name={['medicalAid', 'dependantNumber']}
                style={{ margin: 0 }}
              >
                <Input placeholder='e.g. 01' />
              </Form.Item>
            </div>
          </div>
        </Col>
      </>
    )
  }
)

MedicalAidSection.propTypes = {
  form: PropTypes.object.isRequired,
  hexToRGBA: PropTypes.func.isRequired,
  token: PropTypes.object.isRequired,
  residentVisitor: PropTypes.string,
  schemeData: PropTypes.array.isRequired,
  schemeLoading: PropTypes.bool.isRequired,
  plansData: PropTypes.array.isRequired,
  plansLoading: PropTypes.bool.isRequired,
  planOptionData: PropTypes.array.isRequired,
  planOptionsLoading: PropTypes.bool.isRequired,
  debouncedSchemeFilter: PropTypes.func.isRequired,
  setSchemeData: PropTypes.func.isRequired,
  setPlansData: PropTypes.func.isRequired,
  setPlanOptionData: PropTypes.func.isRequired,
  handleGetMedicalAidPlans: PropTypes.func.isRequired,
  handleGetMedicalAidPlanOptions: PropTypes.func.isRequired,
  userData: PropTypes.object.isRequired
}

MedicalAidSection.displayName = 'MedicalAidSection'

export default MedicalAidSection
